import { useState, useEffect, useCallback, useRef } from 'react'

type UsePollingProps<T, U, E> = {
    hook: {
        fetchHook: (params: U) => {
            data: T | null
            isLoading: boolean
            error: E | null
            refetch: () => void
        }
        params: U
    }
    options: {
        interval: number
        duration?: number
    }
}

export function usePolling<T, U, E = unknown>({
    hook: { fetchHook, params },
    options,
}: UsePollingProps<T, U, E>) {
    const { data, isLoading, error, refetch } = fetchHook(params)
    const [elapsedTime, setElapsedTime] = useState(0)
    const intervalRef = useRef<number>()
    const [isPolling, setIsPolling] = useState<boolean>()

    const stop = useCallback(() => {
        window.clearInterval(intervalRef.current)
        setIsPolling(false)
    }, [])

    const poll = useCallback(() => {
        if (!error && (options.duration === undefined || elapsedTime < options.duration)) {
            refetch()
            setElapsedTime(prev => prev + options.interval)
        }
        if (options.duration && elapsedTime >= options.duration) {
            stop()
        }
    }, [error, options.duration, options.interval, elapsedTime, refetch, stop])

    useEffect(() => {
        setIsPolling(true)
        intervalRef.current = window.setInterval(poll, options.interval)
        return () => stop()
    }, [poll, options.interval, stop])

    return { data, isLoading, error, elapsedTime, stop, isPolling }
}
